import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface SystemMetrics {
  cpu_usage: number
  memory_usage: number
  disk_usage: number
  network_in_mbps: number
  network_out_mbps: number
  active_connections: number
}

interface DatabaseMetrics {
  active_connections: number
  queries_per_second: number
  avg_query_time_ms: number
  slow_queries: number
  connection_pool_usage: number
}

interface CacheMetrics {
  hit_rate: number
  miss_rate: number
  memory_usage_mb: number
  keys_count: number
  evictions: number
}

interface ApiMetrics {
  requests_per_second: number
  avg_response_time_ms: number
  p95_response_time_ms: number
  p99_response_time_ms: number
  error_rate: number
  active_requests: number
}

interface HealthCheck {
  name: string
  status: 'healthy' | 'degraded' | 'unhealthy'
  last_check: string
  response_time_ms: number
  details?: Record<string, any>
}

interface Alert {
  id: string
  severity: 'info' | 'warning' | 'critical'
  type: string
  message: string
  metric: string
  value: number | string
  threshold: number | string
  timestamp: string
  acknowledged: boolean
}

interface Statistics {
  uptime_percentage: number
  uptime_hours: number
  total_requests_24h: number
  total_errors_24h: number
  avg_response_time_24h: number
  p95_response_time_24h: number
  active_alerts: number
  services_healthy: number
  services_total: number
  cache_hit_rate: number
  database_connections: number
}

const API_BASE = '/api'

export default function PerformanceMonitoringPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'system' | 'api' | 'database' | 'alerts'>('overview')
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null)
  const [databaseMetrics, setDatabaseMetrics] = useState<DatabaseMetrics | null>(null)
  const [cacheMetrics, setCacheMetrics] = useState<CacheMetrics | null>(null)
  const [apiMetrics, setApiMetrics] = useState<ApiMetrics | null>(null)
  const [healthChecks, setHealthChecks] = useState<Record<string, HealthCheck>>({})
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)

  useEffect(() => {
    fetchAllData()

    if (autoRefresh) {
      const interval = setInterval(fetchAllData, 30000) // Refresh every 30 seconds
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const fetchAllData = async () => {
    try {
      setLoading(true)
      const [statsRes, currentRes, healthRes, alertsRes] = await Promise.all([
        fetch(`${API_BASE}/monitoring/statistics`),
        fetch(`${API_BASE}/monitoring/current`),
        fetch(`${API_BASE}/monitoring/health-checks`),
        fetch(`${API_BASE}/monitoring/alerts?limit=20`)
      ])

      const stats = await statsRes.json()
      const current = await currentRes.json()
      const health = await healthRes.json()
      const alertsData = await alertsRes.json()

      setStatistics(stats)
      setSystemMetrics(current.system)
      setDatabaseMetrics(current.database)
      setCacheMetrics(current.cache)
      setApiMetrics(current.api)
      setHealthChecks(health.services || {})
      setAlerts(alertsData.alerts || [])
    } catch (error) {
      console.error('Error fetching monitoring data:', error)
    } finally {
      setLoading(false)
    }
  }

  const acknowledgeAlert = async (alertId: string) => {
    try {
      await fetch(`${API_BASE}/monitoring/alerts/${alertId}/acknowledge`, {
        method: 'POST'
      })
      fetchAllData()
    } catch (error) {
      console.error('Error acknowledging alert:', error)
    }
  }

  const getStatusColor = (value: number, thresholds: { warning: number; critical: number }) => {
    if (value >= thresholds.critical) return 'text-red-500'
    if (value >= thresholds.warning) return 'text-orange-500'
    return 'text-green-500'
  }

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500'
      case 'degraded': return 'bg-orange-500'
      case 'unhealthy': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200'
      case 'warning': return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatUptime = (hours: number) => {
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return `${days}d ${remainingHours}h`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="text-sm text-gray-500">Uptime</div>
          <div className="text-2xl font-bold text-green-600">{statistics?.uptime_percentage}%</div>
          <div className="text-sm text-gray-400">{formatUptime(statistics?.uptime_hours || 0)}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Requests (24h)</div>
          <div className="text-2xl font-bold">{statistics?.total_requests_24h?.toLocaleString()}</div>
          <div className="text-sm text-gray-400">Errors: {statistics?.total_errors_24h}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Avg Response Time</div>
          <div className={`text-2xl font-bold ${getStatusColor(statistics?.avg_response_time_24h || 0, { warning: 100, critical: 200 })}`}>
            {statistics?.avg_response_time_24h}ms
          </div>
          <div className="text-sm text-gray-400">P95: {statistics?.p95_response_time_24h}ms</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Active Alerts</div>
          <div className={`text-2xl font-bold ${statistics?.active_alerts && statistics.active_alerts > 0 ? 'text-orange-500' : 'text-green-600'}`}>
            {statistics?.active_alerts}
          </div>
          <div className="text-sm text-gray-400">
            Services: {statistics?.services_healthy}/{statistics?.services_total}
          </div>
        </div>
      </div>

      {/* System Resources */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">System Resources</h3>
        <div className="grid grid-cols-4 gap-6">
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600">CPU</span>
              <span className={`text-sm font-medium ${getStatusColor(systemMetrics?.cpu_usage || 0, { warning: 70, critical: 90 })}`}>
                {systemMetrics?.cpu_usage?.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${(systemMetrics?.cpu_usage || 0) > 90 ? 'bg-red-500' : (systemMetrics?.cpu_usage || 0) > 70 ? 'bg-orange-500' : 'bg-green-500'}`}
                style={{ width: `${systemMetrics?.cpu_usage || 0}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600">Memory</span>
              <span className={`text-sm font-medium ${getStatusColor(systemMetrics?.memory_usage || 0, { warning: 75, critical: 90 })}`}>
                {systemMetrics?.memory_usage?.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${(systemMetrics?.memory_usage || 0) > 90 ? 'bg-red-500' : (systemMetrics?.memory_usage || 0) > 75 ? 'bg-orange-500' : 'bg-green-500'}`}
                style={{ width: `${systemMetrics?.memory_usage || 0}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600">Disk</span>
              <span className={`text-sm font-medium ${getStatusColor(systemMetrics?.disk_usage || 0, { warning: 80, critical: 95 })}`}>
                {systemMetrics?.disk_usage?.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${(systemMetrics?.disk_usage || 0) > 95 ? 'bg-red-500' : (systemMetrics?.disk_usage || 0) > 80 ? 'bg-orange-500' : 'bg-green-500'}`}
                style={{ width: `${systemMetrics?.disk_usage || 0}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600">Network</span>
              <span className="text-sm font-medium text-blue-600">
                {systemMetrics?.active_connections} conn
              </span>
            </div>
            <div className="text-xs text-gray-500">
              In: {systemMetrics?.network_in_mbps?.toFixed(1)} Mbps | Out: {systemMetrics?.network_out_mbps?.toFixed(1)} Mbps
            </div>
          </div>
        </div>
      </div>

      {/* Health Checks */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Service Health</h3>
        <div className="grid grid-cols-5 gap-4">
          {Object.entries(healthChecks).map(([key, check]) => (
            <div key={key} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-3 h-3 rounded-full ${getHealthStatusColor(check.status)}`}></div>
                <span className="font-medium text-sm">{check.name}</span>
              </div>
              <div className="text-xs text-gray-500">
                {check.response_time_ms}ms | {check.status}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Alerts */}
      {alerts.filter(a => !a.acknowledged).length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Active Alerts</h3>
          <div className="space-y-2">
            {alerts.filter(a => !a.acknowledged).slice(0, 5).map((alert) => (
              <div key={alert.id} className={`border rounded-lg p-3 ${getSeverityColor(alert.severity)}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-medium">{alert.message}</span>
                    <div className="text-xs mt-1">
                      {alert.metric}: {alert.value} (threshold: {alert.threshold})
                    </div>
                  </div>
                  <button
                    onClick={() => acknowledgeAlert(alert.id)}
                    className="text-xs underline hover:no-underline"
                  >
                    Acknowledge
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* CPU Details */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">CPU Usage</h3>
          <div className="text-4xl font-bold mb-4 ${getStatusColor(systemMetrics?.cpu_usage || 0, { warning: 70, critical: 90 })}">
            {systemMetrics?.cpu_usage?.toFixed(1)}%
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div
              className="h-4 rounded-full bg-blue-500"
              style={{ width: `${systemMetrics?.cpu_usage || 0}%` }}
            ></div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Cores:</span>
              <span className="ml-2 font-medium">8</span>
            </div>
            <div>
              <span className="text-gray-500">Load Avg:</span>
              <span className="ml-2 font-medium">1.2, 1.5, 1.8</span>
            </div>
          </div>
        </div>

        {/* Memory Details */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Memory Usage</h3>
          <div className="text-4xl font-bold mb-4">
            {systemMetrics?.memory_usage?.toFixed(1)}%
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div
              className="h-4 rounded-full bg-purple-500"
              style={{ width: `${systemMetrics?.memory_usage || 0}%` }}
            ></div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total:</span>
              <span className="ml-2 font-medium">32 GB</span>
            </div>
            <div>
              <span className="text-gray-500">Available:</span>
              <span className="ml-2 font-medium">11.2 GB</span>
            </div>
          </div>
        </div>

        {/* Disk Details */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Disk Usage</h3>
          <div className="text-4xl font-bold mb-4">
            {systemMetrics?.disk_usage?.toFixed(1)}%
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div
              className="h-4 rounded-full bg-orange-500"
              style={{ width: `${systemMetrics?.disk_usage || 0}%` }}
            ></div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total:</span>
              <span className="ml-2 font-medium">500 GB</span>
            </div>
            <div>
              <span className="text-gray-500">Available:</span>
              <span className="ml-2 font-medium">225 GB</span>
            </div>
          </div>
        </div>

        {/* Network Details */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Network</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-500">Inbound</div>
              <div className="text-2xl font-bold text-green-600">{systemMetrics?.network_in_mbps?.toFixed(1)} Mbps</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Outbound</div>
              <div className="text-2xl font-bold text-blue-600">{systemMetrics?.network_out_mbps?.toFixed(1)} Mbps</div>
            </div>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Active Connections:</span>
            <span className="ml-2 font-medium">{systemMetrics?.active_connections}</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderApiTab = () => (
    <div className="space-y-6">
      {/* API Overview */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="text-sm text-gray-500">Requests/sec</div>
          <div className="text-2xl font-bold">{apiMetrics?.requests_per_second?.toFixed(0)}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Avg Response</div>
          <div className={`text-2xl font-bold ${getStatusColor(apiMetrics?.avg_response_time_ms || 0, { warning: 100, critical: 200 })}`}>
            {apiMetrics?.avg_response_time_ms?.toFixed(0)}ms
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">P95 Response</div>
          <div className="text-2xl font-bold">{apiMetrics?.p95_response_time_ms?.toFixed(0)}ms</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Error Rate</div>
          <div className={`text-2xl font-bold ${(apiMetrics?.error_rate || 0) > 1 ? 'text-red-500' : 'text-green-600'}`}>
            {apiMetrics?.error_rate?.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Response Time Distribution */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Response Time Percentiles</h3>
        <div className="flex items-end gap-4 h-32">
          <div className="flex-1 flex flex-col items-center">
            <div className="w-full bg-green-500 rounded-t" style={{ height: `${(apiMetrics?.avg_response_time_ms || 0) / 3}px` }}></div>
            <span className="text-xs mt-2">Avg</span>
            <span className="text-sm font-medium">{apiMetrics?.avg_response_time_ms?.toFixed(0)}ms</span>
          </div>
          <div className="flex-1 flex flex-col items-center">
            <div className="w-full bg-blue-500 rounded-t" style={{ height: `${(apiMetrics?.p95_response_time_ms || 0) / 3}px` }}></div>
            <span className="text-xs mt-2">P95</span>
            <span className="text-sm font-medium">{apiMetrics?.p95_response_time_ms?.toFixed(0)}ms</span>
          </div>
          <div className="flex-1 flex flex-col items-center">
            <div className="w-full bg-orange-500 rounded-t" style={{ height: `${(apiMetrics?.p99_response_time_ms || 0) / 3}px` }}></div>
            <span className="text-xs mt-2">P99</span>
            <span className="text-sm font-medium">{apiMetrics?.p99_response_time_ms?.toFixed(0)}ms</span>
          </div>
        </div>
      </div>

      {/* Active Requests */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Current Activity</h3>
        <div className="text-4xl font-bold text-blue-600">{apiMetrics?.active_requests}</div>
        <div className="text-sm text-gray-500">Active requests being processed</div>
      </div>
    </div>
  )

  const renderDatabaseTab = () => (
    <div className="space-y-6">
      {/* Database Overview */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="text-sm text-gray-500">Connections</div>
          <div className="text-2xl font-bold">{databaseMetrics?.active_connections}</div>
          <div className="text-xs text-gray-400">Pool: {databaseMetrics?.connection_pool_usage?.toFixed(0)}%</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Queries/sec</div>
          <div className="text-2xl font-bold">{databaseMetrics?.queries_per_second?.toFixed(0)}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Avg Query Time</div>
          <div className="text-2xl font-bold">{databaseMetrics?.avg_query_time_ms?.toFixed(0)}ms</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Slow Queries</div>
          <div className={`text-2xl font-bold ${(databaseMetrics?.slow_queries || 0) > 5 ? 'text-orange-500' : 'text-green-600'}`}>
            {databaseMetrics?.slow_queries}
          </div>
        </div>
      </div>

      {/* Cache Performance */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Cache Performance</h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-500">Hit Rate</div>
            <div className="text-2xl font-bold text-green-600">{cacheMetrics?.hit_rate?.toFixed(1)}%</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Miss Rate</div>
            <div className="text-2xl font-bold text-orange-500">{cacheMetrics?.miss_rate?.toFixed(1)}%</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Memory</div>
            <div className="text-2xl font-bold">{cacheMetrics?.memory_usage_mb?.toFixed(0)} MB</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Keys</div>
            <div className="text-2xl font-bold">{cacheMetrics?.keys_count?.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Connection Pool */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Connection Pool Usage</h3>
        <div className="w-full bg-gray-200 rounded-full h-6">
          <div
            className="h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm"
            style={{ width: `${databaseMetrics?.connection_pool_usage || 0}%` }}
          >
            {databaseMetrics?.connection_pool_usage?.toFixed(0)}%
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-500">
          {databaseMetrics?.active_connections} active / 50 max connections
        </div>
      </div>
    </div>
  )

  const renderAlertsTab = () => (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-6 border-l-4 border-red-500">
          <div className="text-sm text-gray-500">Critical</div>
          <div className="text-2xl font-bold text-red-500">
            {alerts.filter(a => a.severity === 'critical' && !a.acknowledged).length}
          </div>
        </div>
        <div className="card p-6 border-l-4 border-orange-500">
          <div className="text-sm text-gray-500">Warning</div>
          <div className="text-2xl font-bold text-orange-500">
            {alerts.filter(a => a.severity === 'warning' && !a.acknowledged).length}
          </div>
        </div>
        <div className="card p-6 border-l-4 border-blue-500">
          <div className="text-sm text-gray-500">Info</div>
          <div className="text-2xl font-bold text-blue-500">
            {alerts.filter(a => a.severity === 'info' && !a.acknowledged).length}
          </div>
        </div>
      </div>

      {/* Alert List */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">All Alerts</h3>
        <div className="space-y-3">
          {alerts.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No alerts at this time</p>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className={`border rounded-lg p-4 ${alert.acknowledged ? 'opacity-50' : ''} ${getSeverityColor(alert.severity)}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${
                        alert.severity === 'critical' ? 'bg-red-200 text-red-800' :
                        alert.severity === 'warning' ? 'bg-orange-200 text-orange-800' :
                        'bg-blue-200 text-blue-800'
                      }`}>
                        {alert.severity}
                      </span>
                      <span className="text-sm text-gray-500">{alert.type}</span>
                    </div>
                    <p className="font-medium mt-1">{alert.message}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Metric: {alert.metric} | Value: {alert.value} | Threshold: {alert.threshold}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(alert.timestamp)}</p>
                  </div>
                  {!alert.acknowledged && (
                    <button
                      onClick={() => acknowledgeAlert(alert.id)}
                      className="btn btn-sm btn-outline"
                    >
                      Acknowledge
                    </button>
                  )}
                  {alert.acknowledged && (
                    <span className="text-xs text-green-600">Acknowledged</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Performance Monitoring</h1>
            <p className="text-gray-600">System health and performance metrics</p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (30s)
            </label>
            <button onClick={fetchAllData} className="btn btn-outline" disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          {['overview', 'system', 'api', 'database', 'alerts'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`pb-2 px-1 capitalize ${activeTab === tab ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            >
              {tab}
              {tab === 'alerts' && alerts.filter(a => !a.acknowledged).length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">
                  {alerts.filter(a => !a.acknowledged).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && !systemMetrics ? (
          <div className="card p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading monitoring data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'system' && renderSystemTab()}
            {activeTab === 'api' && renderApiTab()}
            {activeTab === 'database' && renderDatabaseTab()}
            {activeTab === 'alerts' && renderAlertsTab()}
          </>
        )}
      </main>

      <style>{`
        .btn {
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        .btn-outline {
          border: 1px solid #d1d5db;
          background: white;
        }
        .btn-outline:hover {
          background: #f3f4f6;
        }
        .btn-outline:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.875rem;
        }
        .card {
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  )
}
